import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import api from "@/utils/api";
import { CheckCircle2, AlertCircle, Package } from "lucide-react";

const GRADES = ["CRCA-D", "HRCA-D", "Aluminium", "CuZn37 Brass", "ETP Copper", "Felt", "Other"];
const MATERIAL_TYPES = ["Coil", "Strip", "Sheet"];

const RMReceipt = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    supplier_name: "", invoice_no: "", invoice_date: "", mtc_no: "",
    grade: "", material_type: "", thickness_mm: "", width_mm: "",
    quantity_kg: "", no_of_coils: "", weight_per_coil_kg: "", rate_per_kg: "", remarks: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const totalValue = form.quantity_kg && form.rate_per_kg
    ? (parseFloat(form.quantity_kg) * parseFloat(form.rate_per_kg)).toFixed(2)
    : "";

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError("");

    // Manual validation (submit button lives outside <form>, so HTML5 `required`
    // doesn't fire). Verify every required field is a valid number/string.
    const requiredStr = ["supplier_name", "grade", "material_type"];
    for (const k of requiredStr) {
      if (!form[k] || !String(form[k]).trim()) {
        setError("Please fill all required fields (marked *)");
        return;
      }
    }
    const requiredNum = { thickness_mm: 0.01, width_mm: 1, quantity_kg: 0.1, no_of_coils: 1, rate_per_kg: 0.01 };
    for (const [k, min] of Object.entries(requiredNum)) {
      const v = parseFloat(form[k]);
      if (!Number.isFinite(v) || v < min) {
        setError(`Please enter a valid ${k.replace(/_/g, " ")}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        thickness_mm: parseFloat(form.thickness_mm),
        width_mm: parseFloat(form.width_mm),
        quantity_kg: parseFloat(form.quantity_kg),
        no_of_coils: parseInt(form.no_of_coils),
        weight_per_coil_kg: form.weight_per_coil_kg ? parseFloat(form.weight_per_coil_kg) : 0,
        rate_per_kg: parseFloat(form.rate_per_kg),
      };
      const res = await api.post("/rm-receipt", payload);
      setSuccess(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Submission failed";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) {
        msg = detail
          .map((d) => `${(d.loc || []).slice(-1)[0] || "field"}: ${d.msg || "invalid"}`)
          .join(", ");
      } else if (detail && typeof detail === "object") msg = detail.msg || JSON.stringify(detail);
      else if (err.message) msg = err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header title="RM Receipt" showBack backPath="/home" />
        <div className="max-w-md mx-auto px-4 py-12 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={48} className="text-[#15803D]" />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-2">RM Receipt Submitted!</h2>
          <p className="text-slate-500 mb-4">कच्चा माल रसीद जमा हो गई</p>
          <div className="bg-[#1A3560] rounded-2xl p-6 text-white mb-4">
            <p className="text-white/60 text-sm mb-2">Batch Code / बैच कोड</p>
            <p className="text-3xl font-extrabold tracking-widest">{success.batch_code}</p>
            <p className="text-white/50 text-xs mt-2">Screenshot this code — स्क्रीनशॉट लें</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-left space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Receipt ID</span>
              <span className="font-bold">{success.receipt_id}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Total Value</span>
              <span className="font-bold text-[#15803D]">₹ {parseFloat(success.total_value).toLocaleString("en-IN")}</span>
            </div>
          </div>
          <button
            data-testid="rm-done-btn"
            onClick={() => navigate("/home")}
            className="mt-6 w-full h-12 bg-[#1A3560] text-white font-bold rounded-xl active:scale-[0.98] transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <Header title="RM Receipt" showBack backPath="/home" />

      <div className="max-w-md mx-auto px-4 py-4">
        <div className="bg-[#1A3560] rounded-2xl p-4 text-white mb-4 flex items-center gap-3">
          <Package size={28} className="text-white/70" />
          <div>
            <h2 className="font-extrabold text-lg">Raw Material Receipt</h2>
            <p className="text-white/60 text-xs">कच्चा माल रसीद</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Supplier */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Supplier Details</p>
            <Field label="Supplier Name / आपूर्तिकर्ता" required>
              <input data-testid="supplier-name" value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} required
                placeholder="Supplier name" className={INPUT_CLS} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Invoice No">
                <input data-testid="invoice-no" value={form.invoice_no} onChange={(e) => set("invoice_no", e.target.value)}
                  placeholder="INV-001" className={INPUT_CLS} />
              </Field>
              <Field label="Invoice Date">
                <input data-testid="invoice-date" type="date" value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)}
                  className={INPUT_CLS} />
              </Field>
            </div>
            <Field label="MTC No">
              <input data-testid="mtc-no" value={form.mtc_no} onChange={(e) => set("mtc_no", e.target.value)}
                placeholder="MTC number" className={INPUT_CLS} />
            </Field>
          </div>

          {/* Material */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Material Details</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Grade" required>
                <select data-testid="grade-select" value={form.grade} onChange={(e) => set("grade", e.target.value)} required className={INPUT_CLS}>
                  <option value="">Select</option>
                  {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
              <Field label="Material Type" required>
                <select data-testid="material-type" value={form.material_type} onChange={(e) => set("material_type", e.target.value)} required className={INPUT_CLS}>
                  <option value="">Select</option>
                  {MATERIAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Thickness (mm)" required>
                <input data-testid="thickness-input" type="number" step="0.01" value={form.thickness_mm} onChange={(e) => set("thickness_mm", e.target.value)}
                  required placeholder="0.70" className={INPUT_CLS} />
              </Field>
              <Field label="Width (mm)" required>
                <input data-testid="width-input" type="number" step="0.1" value={form.width_mm} onChange={(e) => set("width_mm", e.target.value)}
                  required placeholder="300" className={INPUT_CLS} />
              </Field>
            </div>
          </div>

          {/* Quantity */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Quantity & Pricing</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Qty (kg)" required>
                <input data-testid="qty-input" type="number" value={form.quantity_kg} onChange={(e) => set("quantity_kg", e.target.value)}
                  required placeholder="1000" className={INPUT_CLS} />
              </Field>
              <Field label="No. of Coils" required>
                <input data-testid="coils-input" type="number" value={form.no_of_coils} onChange={(e) => set("no_of_coils", e.target.value)}
                  required placeholder="5" className={INPUT_CLS} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Wt/Coil (kg)">
                <input data-testid="wt-coil" type="number" step="0.1" value={form.weight_per_coil_kg} onChange={(e) => set("weight_per_coil_kg", e.target.value)}
                  placeholder="200" className={INPUT_CLS} />
              </Field>
              <Field label="Rate (₹/kg)" required>
                <input data-testid="rate-input" type="number" step="0.01" value={form.rate_per_kg} onChange={(e) => set("rate_per_kg", e.target.value)}
                  required placeholder="65.00" className={INPUT_CLS} />
              </Field>
            </div>
            {totalValue && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex justify-between items-center">
                <span className="text-sm text-green-700 font-medium">Total Value</span>
                <span className="text-lg font-extrabold text-[#15803D]">₹ {parseFloat(totalValue).toLocaleString("en-IN")}</span>
              </div>
            )}
          </div>

          {/* Remarks */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <Field label="Remarks (optional)">
              <textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)}
                placeholder="Any additional notes..." rows={2}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1A3560] resize-none" />
            </Field>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={16} /> {error}
            </div>
          )}
        </form>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <div className="max-w-md mx-auto">
          <button
            data-testid="rm-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-14 bg-[#15803D] disabled:bg-slate-300 text-white font-bold text-base rounded-xl active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            {submitting ? (
              <><div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Submitting...</>
            ) : "Submit Receipt / रसीद जमा करें"}
          </button>
        </div>
      </div>
    </div>
  );
};

const INPUT_CLS = "w-full h-12 px-3 rounded-lg border border-slate-300 text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#1A3560]";

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

export default RMReceipt;
